import React, { useState } from 'react';
import { profileAPI } from '../services/apiEndpoints';
import { Button } from './ui/button';
import { Card } from './ui/card';

const NICHES = [
    { value: 'affiliate',  label: 'Affiliate / Reviews',   icon: '📦' },
    { value: 'blog',       label: 'Blog / Editorial',      icon: '✍️' },
    { value: 'news',       label: 'News / Media',          icon: '📰' },
    { value: 'ecommerce',  label: 'E-commerce',            icon: '🛒' },
    { value: 'agency',     label: 'Agency / Client Work',  icon: '🏢' },
    { value: 'other',      label: 'Other',                 icon: '🔬' },
];

const CHALLENGES = {
    affiliate:  'Writing 10 product reviews a week manually takes 40 hours. ContentVertex does it in 30 minutes.',
    blog:       'Your competitors publish daily. ContentVertex lets you match their output at 5% of the effort.',
    news:       'Breaking news doesn\'t wait. ContentVertex turns a trending keyword into a full article in 3 minutes.',
    ecommerce:  'Every product page that isn\'t ranking is revenue you\'re leaving behind. ContentVertex fixes that.',
    agency:     'Delivering client content on time, every time. ContentVertex gives every client a 24-hour turnaround.',
    other:      'Whatever you\'re building, ContentVertex saves you hours of writing every single week.',
};

const Onboarding = ({ onComplete }) => {
    const [step, setStep]   = useState(1);
    const [niche, setNiche] = useState('');
    const [saving, setSaving] = useState(false);

    const handleNicheSelect = (value) => setNiche(value);

    const handleComplete = async () => {
        setSaving(true);
        try {
            await profileAPI.update({ niche, onboarding_complete: true });
        } catch (_) {
            // non-critical — proceed regardless
        }
        setSaving(false);
        onComplete(niche);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
            <div className="w-full max-w-lg mx-4">
                {/* Progress dots */}
                <div className="flex justify-center gap-2 mb-6">
                    {[1, 2, 3].map(s => (
                        <span
                            key={s}
                            className={`h-2 rounded-full transition-all duration-300 ${
                                s === step ? 'w-8 bg-violet-500' : s < step ? 'w-2 bg-violet-500/60' : 'w-2 bg-white/20'
                            }`}
                        />
                    ))}
                </div>

                <Card className="bg-zinc-900 border-white/10 p-8">
                    {step === 1 && (
                        <>
                            <h2 className="text-xl font-semibold text-zinc-100 mb-1">What kind of content do you create?</h2>
                            <p className="text-sm text-zinc-500 mb-6">We'll personalize ContentVertex around your goals.</p>
                            <div className="grid grid-cols-2 gap-3">
                                {NICHES.map(n => (
                                    <button
                                        key={n.value}
                                        onClick={() => handleNicheSelect(n.value)}
                                        className={`flex items-center gap-3 px-4 py-3 rounded-lg border text-left transition-all ${
                                            niche === n.value
                                                ? 'border-violet-500 bg-violet-500/10 text-zinc-100'
                                                : 'border-white/10 hover:border-white/30 text-zinc-400 hover:text-zinc-200'
                                        }`}
                                    >
                                        <span className="text-xl">{n.icon}</span>
                                        <span className="text-sm font-medium">{n.label}</span>
                                    </button>
                                ))}
                            </div>
                            <Button
                                onClick={() => setStep(2)}
                                disabled={!niche}
                                className="w-full mt-6 bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-500 hover:to-blue-500 text-white border-0 disabled:opacity-40"
                            >
                                Continue
                            </Button>
                        </>
                    )}

                    {step === 2 && (
                        <>
                            <h2 className="text-xl font-semibold text-zinc-100 mb-1">Here's how ContentVertex solves your biggest challenge</h2>
                            <p className="text-sm text-zinc-500 mb-6">Specific to {NICHES.find(n => n.value === niche)?.label}.</p>
                            <div className="rounded-lg bg-white/5 border border-white/10 p-5">
                                <p className="text-zinc-200 text-sm leading-relaxed">{CHALLENGES[niche] || CHALLENGES.other}</p>
                            </div>
                            <div className="mt-4 rounded-lg bg-violet-500/10 border border-violet-500/20 px-4 py-3">
                                <p className="text-violet-300 text-xs">
                                    <span className="font-semibold">The prediction:</span> Every article you generate saves ~4 hours and creates an asset worth ~$150 in organic traffic.
                                </p>
                            </div>
                            <div className="flex gap-3 mt-6">
                                <Button
                                    variant="ghost"
                                    onClick={() => setStep(1)}
                                    className="flex-1 text-zinc-500 hover:text-zinc-300"
                                >
                                    Back
                                </Button>
                                <Button
                                    onClick={() => setStep(3)}
                                    className="flex-1 bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-500 hover:to-blue-500 text-white border-0"
                                >
                                    Got it — let's go
                                </Button>
                            </div>
                        </>
                    )}

                    {step === 3 && (
                        <>
                            <h2 className="text-xl font-semibold text-zinc-100 mb-1">You're all set</h2>
                            <p className="text-sm text-zinc-500 mb-6">ContentVertex is ready. Your first article takes 3 minutes.</p>
                            <div className="space-y-3 mb-6">
                                {[
                                    { icon: '✦', label: 'Personalized to your niche' },
                                    { icon: '⚡', label: 'SEO-ready articles in 3 minutes' },
                                    { icon: '📈', label: 'Each article builds your organic traffic' },
                                ].map(item => (
                                    <div key={item.label} className="flex items-center gap-3 text-sm text-zinc-400">
                                        <span className="text-violet-400 font-bold">{item.icon}</span>
                                        {item.label}
                                    </div>
                                ))}
                            </div>
                            <div className="flex gap-3">
                                <Button
                                    variant="ghost"
                                    onClick={() => setStep(2)}
                                    className="text-zinc-500 hover:text-zinc-300"
                                >
                                    Back
                                </Button>
                                <Button
                                    onClick={handleComplete}
                                    disabled={saving}
                                    className="flex-1 bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-500 hover:to-blue-500 text-white border-0"
                                >
                                    {saving ? 'Starting…' : 'Start Creating'}
                                </Button>
                            </div>
                        </>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default Onboarding;
